// src/components/reports/ChartHost.jsx
//
// Chart host component for the report builder.
// Ported and simplified from takso-poc/takso-reporting-ui/src/components/ChartHost.jsx.
//
// Simplifications vs POC:
//   • No LLM prose generation (no chart-prose API call)
//   • No copy-to-clipboard overlay
//
// Props:
//   chart              — key of a ChartRegistry entry (e.g. 'outcomeFrequency')
//   title              — heading text rendered above the chart (also used as PNG filename)
//   activityIds        — number[] — activity set: filter to these specific activities (case 3)
//   programIds         — number[] — legacy: filter to these parent programs (superseded by activityIds)
//   orgId              — number | null — org scope when no activityIds/programIds
//   hideUnused         — boolean — initial value for "Hide unused" (activityTypeLadder only)
//   displayView        — 'ranked' | 'grouped' (activityTypeLadder only)
//   showReportToggle   — boolean — show the "Show in report" Yes/No toggle
//   included           — boolean — initial value for "Show in report" (default true)
//   onReportToggle     — (included: boolean) => void — called when toggle changes
//   onSettingsChange   — ({ hideUnused, displayView }) => void — fired when Hide unused or Display changes
//   reportMode         — boolean — hides all chart-config controls (Grouped/Ranked, Hide unused, Show toggle),
//                        including the pptx field-name badge below
//   showSavePng        — boolean — show "Save PNG" button (left of Show in report)
//
// pptx field-name badge — shown whenever not in reportMode, regardless of
// which other controls apply. Click to copy `tk_chart_<chart>` — the shape
// name a PowerPoint template names a picture placeholder to receive this
// exact chart as an image at export time. See SlidesTab.jsx for the full
// naming convention (tk_chart_*/tk_stat_*/tk_field_*).

import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { ChartRegistry } from '../../charts/registry';

// Charts that expose a "Hide unused" checkbox and "Display Ranked|Grouped" toggle.
const CHARTS_WITH_HIDE_UNUSED = [
  'activityTypeLadder',
  'inputsVenues',
  'inputsPartners',
];
const CHARTS_WITH_DISPLAY_TOGGLE = ['activityTypeLadder'];
// Charts that expose a "Show top N" bar-limit selector.
const CHARTS_WITH_TOP_N = ['outcomeFrequency'];
// Charts that expose a "Show link counts" badge toggle.
const CHARTS_WITH_LINK_COUNTS = ['outcomeNetwork'];
// Charts that expose a "Show node names" label toggle.
const CHARTS_WITH_NODE_LABELS = ['outcomeNetwork'];

class ChartHost extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      data: null,
      hideUnused: props.hideUnused !== undefined ? props.hideUnused : true,
      displayView:
        props.displayView !== undefined ? props.displayView : 'grouped',
      // Defaults to Yes on first mount, matching every other "show in
      // report" toggle in the app. Seedable via props.included so a
      // previously-saved Off state can be restored instead of resetting.
      included: props.included !== undefined ? props.included : true,
      savingPng: false,
      topN: null, // null = All; positive integer = limit bars
      topNCustom: false, // true when "Custom…" is selected in dropdown
      pptxCopied: false,
      showLinkCounts: true,
      showNodeLabels: false,
    };
    this.chartAreaRef = createRef();
    this.handleReportToggle = this.handleReportToggle.bind(this);
    this.handleSavePng = this.handleSavePng.bind(this);
    this.handleCopyPptxLabel = this.handleCopyPptxLabel.bind(this);
  }

  handleCopyPptxLabel() {
    navigator.clipboard?.writeText(`tk_chart_${this.props.chart}`).then(() => {
      this.setState({ pptxCopied: true });
      setTimeout(() => this.setState({ pptxCopied: false }), 1200);
    });
  }

  async handleSavePng() {
    const el = this.chartAreaRef.current;
    if (!el) return;
    this.setState({ savingPng: true });
    try {
      const canvas = await html2canvas(el, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement('a');
      const filename = (this.props.title || 'chart')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      this.setState({ savingPng: false });
    }
  }

  handleReportToggle(value) {
    this.setState({ included: value });
    if (this.props.onReportToggle) this.props.onReportToggle(value);
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    const prevActIds = (prevProps.activityIds || []).join(',');
    const nextActIds = (this.props.activityIds || []).join(',');
    const prevIds = (prevProps.programIds || []).join(',');
    const nextIds = (this.props.programIds || []).join(',');
    const prevFilter = JSON.stringify(prevProps.filterParams || {});
    const nextFilter = JSON.stringify(this.props.filterParams || {});
    if (
      prevActIds !== nextActIds ||
      prevIds !== nextIds ||
      prevFilter !== nextFilter ||
      prevProps.orgId !== this.props.orgId ||
      // Bumped by the parent on tab-visibility-regain — this chart otherwise
      // never refetches once mounted, so without this a colleague's edits
      // to the activity would go unnoticed for as long as the tab stays open.
      prevProps.refreshToken !== this.props.refreshToken
    ) {
      this.fetchData();
    }
  }

  async fetchData() {
    const { chart, activityIds, programIds, orgId, filterParams } = this.props;
    const def = ChartRegistry[chart];
    if (!def || !def.fetch) return;

    this.setState({ loading: true, error: null });

    const flags = {
      activityIds: activityIds || [],
      programIds: programIds || [],
      orgId: orgId || null,
      filterParams: filterParams || {},
    };

    try {
      const data = await def.fetch(flags);
      this.setState({ loading: false, data });
      if (this.props.onData) this.props.onData(data);
    } catch (e) {
      this.setState({
        loading: false,
        error: e?.message || String(e),
        data: null,
      });
    }
  }

  render() {
    const { t, chart } = this.props;
    const {
      loading,
      error,
      data,
      hideUnused,
      displayView,
      included,
      savingPng,
      topN,
      topNCustom,
      pptxCopied,
      showLinkCounts,
      showNodeLabels,
    } = this.state;

    const def = ChartRegistry[chart];
    if (!def) {
      return (
        <div className="ch__error">
          {t('charts.chart_host.unknown_chart')}: {chart}
        </div>
      );
    }

    const reportMode = !!this.props.reportMode;
    const showHideUnused =
      !reportMode && CHARTS_WITH_HIDE_UNUSED.includes(chart);
    const showDisplayToggle =
      !reportMode && CHARTS_WITH_DISPLAY_TOGGLE.includes(chart);
    const showReportToggle = !reportMode && !!this.props.showReportToggle;
    const showSavePng = !reportMode && !!this.props.showSavePng;
    const showTopN = !reportMode && CHARTS_WITH_TOP_N.includes(chart);
    const showLinkCountsToggle =
      !reportMode && CHARTS_WITH_LINK_COUNTS.includes(chart);
    const showNodeLabelsToggle =
      !reportMode && CHARTS_WITH_NODE_LABELS.includes(chart);
    const showPptxLabel = !reportMode;
    const showControls =
      showHideUnused ||
      showDisplayToggle ||
      showReportToggle ||
      showSavePng ||
      showTopN ||
      showLinkCountsToggle ||
      showNodeLabelsToggle ||
      showPptxLabel;

    if (loading) {
      return (
        <div className="ch__loading">
          {t('charts.chart_host.loading_chart')}…
        </div>
      );
    }

    if (error) {
      return (
        <div className="ch__error">
          {t('charts.chart_host.error_loading_chart')}: {error}
        </div>
      );
    }

    return (
      <div className="ch">
        {/* Controls row */}
        {showControls && (
          <div className="ch__controls">
            <div className="ch__left">
              {/* pptx field-name badge — click to copy the picture-placeholder
                  shape name a PowerPoint template uses to receive this chart */}
              {showPptxLabel && (
                <button
                  type="button"
                  className="ch__pptx-label"
                  onClick={this.handleCopyPptxLabel}
                  title={t('charts.chart_host.copy_picture_in_ppt_template')}
                >
                  <span className="ch__pptx-label-tag">pptx</span>
                  <svg
                    className="ch__pptx-label-icon"
                    width="11"
                    height="11"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="5.5"
                      y="5.5"
                      width="9"
                      height="9"
                      rx="1.5"
                      stroke="currentColor"
                      strokeWidth="1.3"
                    />
                    <path
                      d="M3 10.5V2.5A1.5 1.5 0 0 1 4.5 1H10"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                    />
                  </svg>
                  <code>tk_chart_{chart}</code>
                  {pptxCopied && (
                    <span className="ch__pptx-label-copied">
                      {t('actions.copied')}
                    </span>
                  )}
                </button>
              )}
              {/* Optional note — populated via noteFromData prop */}
              {this.props.noteFromData && data && (
                <span className="ch__note">
                  {this.props.noteFromData(data)}
                </span>
              )}
            </div>
            {/* Display toggle — only for activityTypeLadder */}
            {showDisplayToggle && (
              <div className="ch__display">
                <span className="ch__display-label">
                  {t('charts.chart_host.display')}
                </span>
                <button
                  className="ch__display-btn"
                  onClick={() =>
                    this.setState(
                      (s) => ({
                        displayView:
                          s.displayView === 'ranked' ? 'grouped' : 'ranked',
                      }),
                      () =>
                        this.props.onSettingsChange?.({
                          hideUnused: this.state.hideUnused,
                          displayView: this.state.displayView,
                        })
                    )
                  }
                >
                  {displayView === 'ranked'
                    ? t('charts.display_view.ranked')
                    : t('charts.display_view.grouped')}
                </button>
              </div>
            )}

            {/* Hide unused checkbox */}
            {showHideUnused && (
              <label className="ch__hide-unused">
                <input
                  type="checkbox"
                  checked={hideUnused}
                  onChange={(e) =>
                    this.setState({ hideUnused: e.target.checked }, () =>
                      this.props.onSettingsChange?.({
                        hideUnused: this.state.hideUnused,
                        displayView: this.state.displayView,
                      })
                    )
                  }
                />
                {t('common.hide_unused')}
              </label>
            )}

            {/* Show link counts — outcomeNetwork only */}
            {showLinkCountsToggle && (
              <label className="ch__hide-unused">
                <input
                  type="checkbox"
                  checked={showLinkCounts}
                  onChange={(e) =>
                    this.setState({ showLinkCounts: e.target.checked })
                  }
                />
                {t('charts.chart_host.show_link_counts')}
              </label>
            )}

            {/* Show node names — outcomeNetwork only */}
            {showNodeLabelsToggle && (
              <label className="ch__hide-unused">
                <input
                  type="checkbox"
                  checked={showNodeLabels}
                  onChange={(e) =>
                    this.setState({ showNodeLabels: e.target.checked })
                  }
                />
                {t('charts.chart_host.show_node_names')}
              </label>
            )}

            {/* Top-N bar limit — outcomeFrequency only */}
            {showTopN && (
              <div className="ch__top-n">
                <span className="ch__top-n-label">{t('common.show')}</span>
                <select
                  className="ch__top-n-select sml"
                  value={topNCustom ? 'custom' : (topN ?? '')}
                  onChange={(e) => {
                    if (e.target.value === 'custom') {
                      this.setState({ topNCustom: true });
                    } else {
                      this.setState({
                        topNCustom: false,
                        topN: e.target.value ? Number(e.target.value) : null,
                      });
                    }
                  }}
                >
                  <option value="">{t('common.all')}</option>
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="20">20</option>
                  <option value="custom">{t('common.custom')}…</option>
                </select>
                {topNCustom && (
                  <input
                    type="number"
                    min={1}
                    className="ch__top-n-input sml"
                    value={topN ?? ''}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      this.setState({ topN: v > 0 ? v : null });
                    }}
                    placeholder="n"
                  />
                )}
              </div>
            )}

            {/* Save PNG button */}
            {showSavePng && (
              <button
                className="ch__save-png"
                onClick={this.handleSavePng}
                disabled={savingPng}
              >
                {savingPng
                  ? `${t('common.saving')}…`
                  : `⬇ ${t('common.save_png')}`}
              </button>
            )}

            {/* Show in report toggle */}
            {showReportToggle && (
              <div className="ch__include">
                <span className="ch__include-label">
                  {t('activity.report.show_in_report')}
                </span>
                <div className="ch__include-btns">
                  <button
                    className={`ch__include-btn ch__include-btn--yes ${
                      included
                        ? 'ch__include-btn--active'
                        : 'ch__include-btn--inactive'
                    }`}
                    onClick={() => {
                      if (!included) this.handleReportToggle(true);
                    }}
                  >
                    {t('actions.yes')}
                  </button>
                  <button
                    className={`ch__include-btn ${
                      !included
                        ? 'ch__include-btn--active'
                        : 'ch__include-btn--inactive'
                    }`}
                    onClick={() => {
                      if (included) this.handleReportToggle(false);
                    }}
                  >
                    {t('actions.no')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chart render area */}
        <div ref={this.chartAreaRef}>
          {def.render({
            data,
            title: '',
            sub: false,
            programIds: this.props.programIds || [],
            hideUnused,
            displayView,
            topN,
            showLinkCounts,
            showNodeLabels,
            onOutcomeBarClick: this.props.onOutcomeBarClick || null,
          })}
        </div>
      </div>
    );
  }
}

ChartHost = withTranslation()(ChartHost);

export default ChartHost;
